import type { Theme } from '#states/UIState.js';

import { useEffect } from 'react';

import { ROUTES } from '@elara/client-core/constants';
import { getTimezoneOptions } from '@elara/client-core/lib/timezone-utils';
import { DEFAULT_CURRENCY, DEFAULT_TIMEZONE } from '@elara/domain/locale';

import { getCurrencyOptions } from '@elara/client-core/lib/currency-utils';
import { getServerApiUrl } from '#lib/server-api-utils.js';
import {
  useCurrentUserProfile,
  useNavigate,
  useRouterState,
  useToastState,
  useUIState,
  useUserProfileState,
} from '#states/hooks.js';
import { Breadcrumbs } from '#ui/Breadcrumbs.js';
import { CopyButton } from '#ui/copy-button.js';
import { Button, Card, CardContent, CardHeader, CardTitle, FloatingSelect, Loader, SelectItem } from '#ui/index.js';
import { AppLayout, AppLayoutTitle } from '#views/layout/AppLayout.jsx';

const TITLE_CLASSES = 'text-xl font-regular';
const TIMEZONE_OPTIONS = getTimezoneOptions();
const CURRENCY_OPTIONS = getCurrencyOptions();

export function SettingsPage() {
  const profile = useCurrentUserProfile();
  const profileState = useUserProfileState();
  const routerState = useRouterState();
  const navigate = useNavigate();
  const uiState = useUIState();
  const toastState = useToastState();

  useEffect(() => {
    if (profile) profileState.initializeEditing();
  }, [profile, profileState]);

  if (!profile) {
    return (
      <AppLayout
        backButtonRoute={ROUTES.ACCOUNT}
        title={<AppLayoutTitle title="Settings" />}
        transitionKey={routerState.currentRoute?.path}
      >
        <div className="flex items-center justify-center h-full">
          <Loader loading={true} />
        </div>
      </AppLayout>
    );
  }

  const isSaving = profileState.isSaving;
  const error = profileState.error;
  const editingProfile = profileState.editingProfile;

  const handleSave = async () => {
    await profileState.save();
    if (!profileState.error) {
      toastState.addToast({ title: 'Settings saved', variant: 'success' });
      navigate.navigate(ROUTES.ACCOUNT);
    }
  };

  return (
    <AppLayout
      backButtonRoute={ROUTES.ACCOUNT}
      title={<AppLayoutTitle title="Settings" />}
      transitionKey={routerState.currentRoute?.path}
    >
      <div className="h-full flex flex-col overflow-hidden">
        <div className="flex-1 overflow-auto">
          <div className="max-w-2xl mx-auto w-full px-4 pt-12 pb-16 flex flex-col gap-6">
            <div className="flex flex-col gap-3 mb-2">
              <Breadcrumbs items={[{ label: 'Account', to: ROUTES.ACCOUNT }, { label: 'Settings' }]} className="mb-3" />
              <h1 className="text-2xl xl:text-3xl font-display font-light">Settings</h1>
              <p className="text-muted-foreground">App settings and interface preferences.</p>
            </div>

            {error && (
              <div className="bg-destructive/10 text-destructive px-4 py-2 rounded-md text-sm mb-6">
                {error.message}
              </div>
            )}

            <Card className="shadow-sm p-6 dark:bg-background-secondary">
              <CardHeader>
                <CardTitle className={TITLE_CLASSES}>Preferences</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <FloatingSelect
                    label="Preferred currency"
                    value={editingProfile.attributes?.preferredCurrency || DEFAULT_CURRENCY}
                    onValueChange={(value) => profileState.setAttribute('preferredCurrency', value)}
                    id="preferredCurrency"
                  >
                    {CURRENCY_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </FloatingSelect>

                  <FloatingSelect
                    label="Timezone"
                    value={editingProfile.attributes?.timezone || DEFAULT_TIMEZONE}
                    onValueChange={(value) => profileState.setAttribute('timezone', value)}
                    id="timezone"
                  >
                    {TIMEZONE_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </FloatingSelect>

                  <div className="space-y-2">
                    <FloatingSelect
                      label="Theme"
                      value={(editingProfile.attributes?.theme as Theme) || 'system'}
                      onValueChange={(value) => {
                        const theme = value as Theme;
                        profileState.setAttribute('theme', theme);
                        uiState.applyTheme(theme);
                      }}
                      id="theme"
                    >
                      <SelectItem value="light">Light</SelectItem>
                      <SelectItem value="dark">Dark</SelectItem>
                      <SelectItem value="system">System</SelectItem>
                    </FloatingSelect>
                    <p className="text-sm text-muted-foreground">
                      Choose your preferred color scheme. System will match your device settings.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {profileState.isPrivileged && profile.mcpKey && <McpDetailsCard mcpKey={profile.mcpKey} />}

            <FormButtonsBar>
              <Button variant="outline" onClick={() => navigate.navigate(ROUTES.ACCOUNT)} disabled={isSaving}>
                Cancel
              </Button>
              <Button onClick={handleSave} isBusy={isSaving}>
                Save
              </Button>
            </FormButtonsBar>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}

function FormButtonsBar({ children }: { children: React.ReactNode }) {
  return (
    <div className="sticky bottom-0 left-0 right-0 py-4 bg-background-secondary/60 backdrop-blur-md -mx-4">
      <div className="flex justify-end gap-3 max-w-2xl mx-auto w-full px-4">{children}</div>
    </div>
  );
}

function McpDetailsCard({ mcpKey }: { mcpKey: string }) {
  const mcpConfigJson = JSON.stringify(mcpConfig({ mcpKey }), null, 4);
  return (
    <Card className="shadow-sm p-6 dark:bg-background-secondary">
      <CardHeader>
        <CardTitle className={TITLE_CLASSES}>
          MCP details <CopyButton size={'icon-sm'} variant={'icon'} content={mcpConfigJson} />
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="text-xs space-y-1 bg-muted p-2 border rounded-sm w-full">
          <pre className="font-mono overflow-x-auto whitespace-pre-wrap break-words">{mcpConfigJson}</pre>
        </div>
      </CardContent>
    </Card>
  );
}

function mcpConfig({ mcpKey }: { mcpKey: string }) {
  const mcpUrl = getServerApiUrl('/mcp');
  return { type: 'http', url: mcpUrl, headers: { Authorization: `Bearer ${mcpKey}` } };
}
